/**
 * Compliance Tracking API Service - RTK Query
 * BuilderBel Construction Project Management System
 */

import { baseApi } from './api';
import { ApiResponse } from '../types';
import type {
  ComplianceItem,
  ComplianceListParams,
  ComplianceListResponse,
  ComplianceStats,
  CreateComplianceItemRequest,
  UpdateComplianceItemRequest,
  VerifyComplianceItemRequest,
} from '../types/compliance';

/**
 * Compliance API Endpoints
 */
export const complianceApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    // =========================================================================
    // LIST
    // =========================================================================

    getComplianceItems: builder.query<
      ApiResponse<ComplianceListResponse>,
      { projectId: string; params?: ComplianceListParams }
    >({
      query: ({ projectId, params }) => ({
        url: `/projects/${projectId}/compliance`,
        params,
      }),
      providesTags: ['Compliance'],
    }),

    // =========================================================================
    // STATS
    // =========================================================================

    getComplianceStats: builder.query<
      ApiResponse<ComplianceStats>,
      { projectId: string }
    >({
      query: ({ projectId }) => ({
        url: `/projects/${projectId}/compliance/stats`,
      }),
      providesTags: ['ComplianceStats'],
    }),

    // =========================================================================
    // GET ONE
    // =========================================================================

    getComplianceItem: builder.query<
      ApiResponse<ComplianceItem>,
      { projectId: string; itemId: string }
    >({
      query: ({ projectId, itemId }) => ({
        url: `/projects/${projectId}/compliance/${itemId}`,
      }),
      providesTags: (_result, _error, { itemId }) => [{ type: 'Compliance', id: itemId }],
    }),

    // =========================================================================
    // CREATE
    // =========================================================================

    createComplianceItem: builder.mutation<
      ApiResponse<ComplianceItem>,
      { projectId: string; data: CreateComplianceItemRequest }
    >({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/compliance`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Compliance', 'ComplianceStats'],
    }),

    // =========================================================================
    // UPDATE
    // =========================================================================

    updateComplianceItem: builder.mutation<
      ApiResponse<ComplianceItem>,
      { projectId: string; itemId: string; data: UpdateComplianceItemRequest }
    >({
      query: ({ projectId, itemId, data }) => ({
        url: `/projects/${projectId}/compliance/${itemId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { itemId }) => [
        'Compliance',
        { type: 'Compliance', id: itemId },
        'ComplianceStats',
      ],
    }),

    // =========================================================================
    // DELETE
    // =========================================================================

    deleteComplianceItem: builder.mutation<
      ApiResponse<void>,
      { projectId: string; itemId: string }
    >({
      query: ({ projectId, itemId }) => ({
        url: `/projects/${projectId}/compliance/${itemId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Compliance', 'ComplianceStats'],
    }),

    // =========================================================================
    // VERIFY
    // =========================================================================

    verifyComplianceItem: builder.mutation<
      ApiResponse<ComplianceItem>,
      { projectId: string; itemId: string; data: VerifyComplianceItemRequest }
    >({
      query: ({ projectId, itemId, data }) => ({
        url: `/projects/${projectId}/compliance/${itemId}/verify`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { itemId }) => [
        'Compliance',
        { type: 'Compliance', id: itemId },
        'ComplianceStats',
      ],
    }),
  }),
});

export const {
  useGetComplianceItemsQuery,
  useGetComplianceStatsQuery,
  useGetComplianceItemQuery,
  useCreateComplianceItemMutation,
  useUpdateComplianceItemMutation,
  useDeleteComplianceItemMutation,
  useVerifyComplianceItemMutation,
} = complianceApi;
